<template>
  <div class="widget-carousel-products-tab">
    <div class="card border-0 shadow-sm compact-form-card">
      <div class="card-header bg-white border-bottom compact-form-card__header">
        <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
        >
          <div class="mb-2 mb-md-0">
            <strong>{{ productsTitle }}</strong>
          </div>

          <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!isWidgetActive"
              @click="openAddProductsModal"
          >
            {{ addButtonLabel }}
          </button>
        </div>
      </div>

      <div class="card-body compact-form-card__body">
        <p class="widget-carousel-products-tab__description">
          {{ $t("superUpLang.widget.carousel.productsTab.description", { item: productLabel.toLowerCase() }) }}
        </p>

        <div
            v-if="!isWidgetActive"
            class="widget-carousel-products-tab__warning"
        >
          {{ $t("superUpLang.widget.carousel.productsTab.warningInactive", { items: productsTitle.toLowerCase() }) }}
        </div>

        <WidgetCarouselProductsList
            :is-disabled="!isWidgetActive"
            @replace-product="openReplaceProductsModal"
        />
      </div>
    </div>

    <product-selector-modal
        v-if="isProductsModalOpen"
        :visible="isProductsModalOpen"
        :type="selectorType"
        @close="closeProductsModal"
        @select="handleProductSelected"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import WidgetCarouselProductsList from './WidgetCarouselProductsList.vue';
import ProductSelectorModal from "@/superUp-module/resources/views/pages/widget/ProductsTable.vue";

const STORE_NAMESPACE = 'widgetCarousel';

export default {
  name: 'WidgetCarouselProductsTab',

  components: {
    ProductSelectorModal,
    WidgetCarouselProductsList,
  },

  data() {
    return {
      isProductsModalOpen: false,
      modalMode: 'add',
      replaceTargetLocalId: null
    };
  },

  computed: {
    ...mapGetters(STORE_NAMESPACE, [
      'isWidgetActive',
      'form'
    ]),

    selectorType() {
      return this.form && this.form.type === 'offers' ? 2 : 1;
    },

    isOffersMode() {
      return this.form && this.form.type === 'offers';
    },

    productLabel() {
      return this.isOffersMode
        ? this.$t('superUpLang.widget.carousel.common.offer')
        : this.$t('superUpLang.widget.carousel.common.product');
    },

    productsTitle() {
      return this.isOffersMode
        ? this.$t('superUpLang.widget.carousel.common.offers')
        : this.$t('superUpLang.widget.carousel.common.products');
    },

    addButtonLabel() {
      return this.isOffersMode
        ? this.$t('superUpLang.widget.carousel.productsTab.actions.addOffer')
        : this.$t('superUpLang.widget.carousel.productsTab.actions.addProduct');
    }
  },

  methods: {
    openAddProductsModal() {
      if (!this.isWidgetActive) {
        return;
      }

      this.modalMode = 'add';
      this.replaceTargetLocalId = null;
      this.isProductsModalOpen = true;
    },

    openReplaceProductsModal(localId) {
      if (!this.isWidgetActive) {
        return;
      }

      this.modalMode = 'replace';
      this.replaceTargetLocalId = localId;
      this.isProductsModalOpen = true;
    },

    closeProductsModal() {
      this.isProductsModalOpen = false;
      this.modalMode = 'add';
      this.replaceTargetLocalId = null;
    },

    handleProductSelected(product) {
      const normalizedProduct = this.normalizeSelectedProduct(product);

      if (this.modalMode === 'replace' && this.replaceTargetLocalId) {
        this.$store.dispatch(`${STORE_NAMESPACE}/replaceProductSource`, {
          localId: this.replaceTargetLocalId,
          source: normalizedProduct
        });

        this.closeProductsModal();
        return;
      }

      this.$store.dispatch(`${STORE_NAMESPACE}/addProduct`, normalizedProduct);
      this.closeProductsModal();
    },

    normalizeSelectedProduct(product) {
      const isOffer = this.isOffersMode;

      return {
        source_type: isOffer ? 'offers' : 'benefits',
        product_id: product && product.id ? product.id : null,
        product_name: product && product.name
            ? String(product.name)
            : (isOffer
                ? this.$t('superUpLang.widget.carousel.productsTab.defaults.newOffer')
                : this.$t('superUpLang.widget.carousel.productsTab.defaults.newProduct')),
        product_name_en: '',
        price: !isOffer && product && product.price != null ? String(product.price) : '',
        discount_price: !isOffer && product && product.reduced_price != null ? String(product.reduced_price) : '',
        user_discount: !isOffer && product && product.user_discount != null ? String(product.user_discount) : '',
        discount: !isOffer && product && product.discount != null ? String(product.discount) : '',
        value: isOffer && product && product.value != null ? String(product.value) : '',
        brand_name: product && product.brand ? String(product.brand) : '',
        is_active: typeof product.is_active === 'boolean' ? product.is_active : true,
        reason: product && product.reason ? String(product.reason) : '',
        image: {
          file: null,
          preview: product && (product.image_url || product.image || product.preview_image)
              ? (product.image_url || product.image || product.preview_image)
              : '',
          error: ''
        },

        corner_radius: 12,
        use_d_shape: false,
        spacing: 4,
        background_color: '',
        margins: {
          top: 8,
          left: 8,
          right: 8,
          bottom: 8
        },
        paddings: {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        },
        items: [
          {
            badges: {
              top_left: [],
              top_right: [],
              bottom_left: [],
              bottom_right: []
            }
          }
        ]
      };
    }
  }
};
</script>

<style scoped>
.compact-form-card__header {
  padding: 10px 14px;
  font-size: 14px;
}

.compact-form-card__body {
  padding: 14px;
}

.widget-carousel-products-tab__description {
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1.45;
  color: #6c757d;
}

.widget-carousel-products-tab__warning {
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  background: #fff5f5;
  color: #b02a37;
  font-size: 12px;
  font-weight: 600;
}
</style>